import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { InGameCurrency } from '#src/__generated__/globalTypes.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { scarcityNames } from '#src/lib/cards.ts';
import { cardPull } from '#src/lib/glossary.ts';
import { isType } from '#src/lib/gql/index.ts';
import type { MonetaryAmount } from '#src/lib/monetaryAmount.ts';
import {
  isCraftingClue,
  isWheelTicket,
  isXPCurrency,
} from '#src/lib/rewards.tsx';
import { inGameCurrencyToRarity, scarcityMessages } from '#src/lib/scarcity.ts';

import type { RewardTitle_AnyRewardInterface } from './__generated__/index.graphql.ts';

type Props = {
  reward: RewardTitle_AnyRewardInterface;
};

const MarketCreditTitle = ({
  monetaryAmount,
  percentageDiscount,
}: {
  monetaryAmount: MonetaryAmount;
  percentageDiscount: number;
}) => {
  const { main } = useAmountWithConversion({
    monetaryAmount,
  });
  return (
    <FormattedMessage
      defaultMessage="You also received a {percentageDiscount} market credit up to {maxDiscount}!"
      values={{
        percentageDiscount: (
          <FormattedNumber value={percentageDiscount} style="percent" />
        ),
        maxDiscount: main,
      }}
    />
  );
};

const clueMessages = {
  [InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE]: cardPull.bestStarRankClue,
  [InGameCurrency.LIMITED_BEST_STAR_RANK_CRAFT_CLUE]: cardPull.bestStarRankClue,
  [InGameCurrency.RARE_BEST_STAR_RANK_CRAFT_CLUE]: cardPull.bestStarRankClue,
  [InGameCurrency.SUPER_RARE_BEST_STAR_RANK_CRAFT_CLUE]:
    cardPull.bestStarRankClue,
  [InGameCurrency.UNIQUE_BEST_STAR_RANK_CRAFT_CLUE]: cardPull.bestStarRankClue,
  [InGameCurrency.COMPETITION_CRAFT_CLUE]: cardPull.competitionClue,
  [InGameCurrency.LIMITED_COMPETITION_CRAFT_CLUE]: cardPull.competitionClue,
  [InGameCurrency.RARE_COMPETITION_CRAFT_CLUE]: cardPull.competitionClue,
  [InGameCurrency.SUPER_RARE_COMPETITION_CRAFT_CLUE]: cardPull.competitionClue,
  [InGameCurrency.UNIQUE_COMPETITION_CRAFT_CLUE]: cardPull.competitionClue,
  [InGameCurrency.COUNTRY_CRAFT_CLUE]: cardPull.countryClue,
  [InGameCurrency.LIMITED_COUNTRY_CRAFT_CLUE]: cardPull.countryClue,
  [InGameCurrency.RARE_COUNTRY_CRAFT_CLUE]: cardPull.countryClue,
  [InGameCurrency.SUPER_RARE_COUNTRY_CRAFT_CLUE]: cardPull.countryClue,
  [InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE]: cardPull.fiftyFiftyClue,
  [InGameCurrency.LIMITED_FIFTY_FIFTY_CRAFT_CLUE]: cardPull.fiftyFiftyClue,
  [InGameCurrency.RARE_FIFTY_FIFTY_CRAFT_CLUE]: cardPull.fiftyFiftyClue,
  [InGameCurrency.SUPER_RARE_FIFTY_FIFTY_CRAFT_CLUE]: cardPull.fiftyFiftyClue,
  [InGameCurrency.UNIQUE_FIFTY_FIFTY_CRAFT_CLUE]: cardPull.fiftyFiftyClue,
  [InGameCurrency.DIVISION_CRAFT_CLUE]: cardPull.divisionClue,
  [InGameCurrency.POSITION_CRAFT_CLUE]: cardPull.positionClue,
  [InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE]: cardPull.globalCupClue,
  [InGameCurrency.LIMITED_SPECIAL_EVENT_CRAFT_CLUE]: cardPull.globalCupClue,
  [InGameCurrency.RARE_SPECIAL_EVENT_CRAFT_CLUE]: cardPull.globalCupClue,
  [InGameCurrency.SUPER_RARE_SPECIAL_EVENT_CRAFT_CLUE]: cardPull.globalCupClue,
  [InGameCurrency.UNIQUE_SPECIAL_EVENT_CRAFT_CLUE]: cardPull.globalCupClue,
};

export const RewardTitle = ({ reward }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  if (isType(reward, 'InGameCurrencyReward')) {
    if (reward.config.currency === InGameCurrency.OLD_CLUB_SHOP_COIN) {
      return (
        <FormattedMessage
          defaultMessage="You also received {amount} club shop coin!"
          values={{
            amount: reward.config.amount,
          }}
        />
      );
    }
    const rarity = inGameCurrencyToRarity[reward.config.currency]!;
    if (isXPCurrency(reward.config.currency)) {
      return (
        <FormattedMessage
          defaultMessage="You also received {amount} {rarity} XP!"
          values={{
            amount: reward.config.amount,
            rarity: <FormattedMessage {...scarcityMessages[rarity]} />,
          }}
        />
      );
    }
    if (isCraftingClue(reward.config.currency)) {
      return (
        <FormattedMessage
          defaultMessage="You also received {amount, plural, =1 {1 crafting clue} other {# crafting clues}}: {clue}!"
          values={{
            clue: (
              <FormattedMessage {...clueMessages[reward.config.currency]} />
            ),
            amount: reward.config.amount,
          }}
        />
      );
    }
    if (isWheelTicket(reward.config.currency)) {
      return (
        <FormattedMessage
          defaultMessage="You also received {amount, plural, =1 {1 wheel ticket} other {# wheel tickets}}!"
          values={{
            amount: reward.config.amount,
          }}
        />
      );
    }
  }
  if (isType(reward, 'AnyCardReward')) {
    return (
      <FormattedMessage
        defaultMessage="Free card, {playerDisplayName} is also joining your team!"
        values={{
          playerDisplayName: reward.card.anyPlayer.displayName,
        }}
      />
    );
  }
  if (isType(reward, 'CardShardsReward')) {
    return (
      <FormattedMessage
        defaultMessage="You also received {quantity} {rarity} essence {quantity, plural, one {capsule} other {capsules}}!"
        values={{
          quantity: reward.quantity,
          rarity: scarcityNames[reward.rarity],
        }}
      />
    );
  }

  if (isType(reward, 'ProbabilisticBundleReward')) {
    return (
      <FormattedMessage
        defaultMessage="You also received a {name}!"
        values={{
          name: reward.probabilisticBundle.config.name,
        }}
      />
    );
  }
  if (isType(reward, 'ShopItemReward')) {
    return (
      <FormattedMessage
        defaultMessage="You also received a {name}!"
        values={{
          name: reward.shopItem.name,
        }}
      />
    );
  }
  if (isType(reward, 'AnyConversionCreditReward')) {
    return (
      <MarketCreditTitle
        monetaryAmount={toMonetaryAmount(reward.conversionCredit.maxDiscount)}
        percentageDiscount={reward.conversionCredit.percentageDiscount}
      />
    );
  }
  if (isType(reward, 'DeliverableItemReward')) {
    const { deliverableItem } = reward;
    if (isType(deliverableItem, 'JerseyDeliverableItem')) {
      if (reward.team) {
        return (
          <FormattedMessage
            defaultMessage="You also received a {team} {jersey}! Fill out details on the home page."
            values={{
              jersey: deliverableItem.name,
              team: reward.team.shortName,
            }}
          />
        );
      }
      return (
        <FormattedMessage
          defaultMessage="You also received a {jersey}! Fill out details on the home page."
          values={{
            jersey: deliverableItem.name,
          }}
        />
      );
    }
    if (isType(deliverableItem, 'TicketsDeliverableItem')) {
      if (reward.team) {
        return (
          <FormattedMessage
            defaultMessage="You also received a {ticketNameAndQuantity} for {team}! Our team will contact you shortly."
            values={{
              ticketNameAndQuantity: deliverableItem.name,
              team: reward.team.shortName,
            }}
          />
        );
      }
      return (
        <FormattedMessage
          defaultMessage="You also received a {name}! Our team will contact you shortly."
          values={{
            name: deliverableItem.name,
          }}
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="You also received a {name}!"
        values={{
          name: deliverableItem.name,
        }}
      />
    );
  }
  return null;
};

RewardTitle.fragments = {
  AnyRewardInterface: gql`
    fragment RewardTitle_AnyRewardInterface on AnyRewardInterface {
      id
      ... on InGameCurrencyReward {
        id
        config {
          id
          amount
          currency
        }
      }
      ... on AnyCardReward {
        id
        card {
          slug
          anyPlayer {
            slug
            displayName
          }
        }
      }
      ... on CardShardsReward {
        id
        quantity
        rarity
      }
      ... on ProbabilisticBundleReward {
        id
        probabilisticBundle {
          id
          config {
            id
            name
          }
        }
      }
      ... on ShopItemReward {
        id
        shopItem {
          id
          name
        }
      }
      ... on AnyConversionCreditReward {
        id
        conversionCredit {
          id
          percentageDiscount
          maxDiscount {
            ...useMonetaryAmount_monetaryAmount
          }
        }
      }
      ... on DeliverableItemReward {
        id
        team {
          slug
          shortName
        }
        deliverableItem {
          slug
          name
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<RewardTitle_AnyRewardInterface>,
};
